/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { debounce } from 'lodash';

import { useSharedTranscript } from '@utils/providers/shared-transcript-context.tsx';
import { useSharedEditorContext } from '@utils/providers/shared-editor-context.tsx';

import Input from '@components/atoms/ui-kit/input.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { DocumentTranscriptUtteranceV1 } from '@utils/types/editors/transcripts/transcript.ts';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';

type DocForSearch = { document: DocumentTranscriptUtteranceV1[] };

/** Single precise hit within a child text node */
type SearchHit = {
  uIdx: number;
  pIdx: number;
  cIdx: number;
  from: number;
  to: number;
  utteranceStart: number;
  utteranceStop: number;
  childText: string;
};

export const SharedTranscriptSearch = () => {
  const { t } = useTranslation();
  const { vars } = useThemeVars();
  const { getColour } = useThemeContext();

  const [replacement, setReplacement] = useState<string>('');

  const {
    search,
    setSearch,
    searchInputRef,
    caseSensitive,
    setCaseSensitive,
    wholeWord,
    setWholeWord,
    setCurrentMatch,
    document,
    updateDocument,
    focusMatch
  } = useSharedTranscript();

  const [query, setQuery] = useState<string>(search ?? '');

  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => setSearch(value), 250),
    [setSearch]
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const handleSearchInput = (v: string) => {
    setQuery(v);
    debouncedSetSearch(v);
  };

  const { replaceAllWordsInEditor, replaceInEditorAtTimestampAndOffsets } =
    useSharedEditorContext();

  /** Scan the CURRENT state doc (keeps legacy behavior) */
  const findHits = useCallback(
    (searchText: string, cs: boolean, ww: boolean): SearchHit[] => {
      if (!searchText || !document) return [];

      const flags = cs ? 'g' : 'gi';
      const pattern = ww ? `\\b${searchText}\\b` : searchText;
      const rx = new RegExp(pattern, flags);

      const hits: SearchHit[] = [];

      document.document.forEach((utt, uIdx) => {
        const uStart = typeof utt.start === 'number' ? utt.start : null;
        const uStop = typeof utt.stop === 'number' ? utt.stop : null;
        if (uStart === null || uStop === null) return;

        utt.children.forEach((para, pIdx) => {
          para.children.forEach((child, cIdx) => {
            const txt = child.text ?? '';
            if (!txt) return;
            rx.lastIndex = 0;
            let m: RegExpExecArray | null;
            while ((m = rx.exec(txt))) {
              hits.push({
                uIdx,
                pIdx,
                cIdx,
                from: m.index,
                to: m.index + m[0].length,
                utteranceStart: uStart,
                utteranceStop: uStop,
                childText: txt
              });
            }
          });
        });
      });

      return hits;
    },
    [document?.document]
  );

  const findHitsInDoc = useCallback(
    (
      doc: DocForSearch | null | undefined,
      searchText: string,
      cs: boolean,
      ww: boolean
    ): SearchHit[] => {
      if (!doc || !searchText) return [];

      const flags = cs ? 'g' : 'gi';
      const pattern = ww ? `\\b${searchText}\\b` : searchText;
      const rx = new RegExp(pattern, flags);

      const hits: SearchHit[] = [];

      doc.document.forEach((utt, uIdx) => {
        const uStart = typeof utt.start === 'number' ? utt.start : null;
        const uStop = typeof utt.stop === 'number' ? utt.stop : null;
        if (uStart === null || uStop === null) return;

        utt.children.forEach((para, pIdx) => {
          para.children.forEach((child, cIdx) => {
            const txt = child.text ?? '';
            if (!txt) return;
            rx.lastIndex = 0;
            let m: RegExpExecArray | null;
            while ((m = rx.exec(txt))) {
              hits.push({
                uIdx,
                pIdx,
                cIdx,
                from: m.index,
                to: m.index + m[0].length,
                utteranceStart: uStart,
                utteranceStop: uStop,
                childText: txt
              });
            }
          });
        });
      });

      return hits;
    },
    []
  );

  /** Local hits + active index */
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [activeHitIndex, setActiveHitIndex] = useState<number>(-1);

  /** Recompute hits on changes (state doc) */
  useEffect(() => {
    if (!search?.trim() || !document) {
      setHits([]);
      setActiveHitIndex(-1);
      setCurrentMatch(null);
      return;
    }
    const next = findHits(search.trim(), !!caseSensitive, !!wholeWord);
    setHits(next);
    setActiveHitIndex(next.length ? 0 : -1);

    if (next.length) {
      const h = next[0];
      const child = document.document[h.uIdx].children[h.pIdx].children[h.cIdx];
      setCurrentMatch(child);
      focusMatch?.(child);
    } else {
      setCurrentMatch(null);
    }
  }, [search, document?.document, caseSensitive, wholeWord, findHits]);

  /** Navigation across all hits */
  const onNext = () => {
    if (!hits.length || !document) return;

    const nextIdx = (activeHitIndex + 1) % hits.length;
    setActiveHitIndex(nextIdx);

    const h = hits[nextIdx];

    const utterance = document.document.find(
      utt => utt.start === h.utteranceStart && utt.stop === h.utteranceStop
    );

    if (utterance && utterance.children[h.pIdx]?.children[h.cIdx]) {
      const child = utterance.children[h.pIdx].children[h.cIdx];
      setCurrentMatch(child);
      focusMatch?.(child);
    }
  };

  const onPrev = () => {
    if (!hits.length || !document) return;

    const prevIdx = (activeHitIndex - 1 + hits.length) % hits.length;
    setActiveHitIndex(prevIdx);

    const h = hits[prevIdx];

    const utterance = document.document.find(
      utt => utt.start === h.utteranceStart && utt.stop === h.utteranceStop
    );

    if (utterance && utterance.children[h.pIdx]?.children[h.cIdx]) {
      const child = utterance.children[h.pIdx].children[h.cIdx];
      setCurrentMatch(child);
      focusMatch?.(child);
    }
  };

  /** Replace only the SINGLE active hit — Slate-first, then recompute using nextDoc */
  const handleReplaceThis = () => {
    if (!document || !hits.length) return;
    if (activeHitIndex < 0 || activeHitIndex >= hits.length) return;

    const h = hits[activeHitIndex];
    const replacementText = replacement ?? '';

    // 1) Update Slate directly (instant UI)
    replaceInEditorAtTimestampAndOffsets(
      h.utteranceStart,
      h.utteranceStop,
      h.from,
      h.to,
      replacementText
    );

    // 2) Build nextDoc immutably and push it to context
    const uIdx = document.document.findIndex(
      utt => utt.start === h.utteranceStart && utt.stop === h.utteranceStop
    );
    if (uIdx !== -1) {
      const utt = document.document[uIdx];
      const para = utt.children[h.pIdx];
      const child = para?.children[h.cIdx];
      if (child) {
        const newText =
          (child.text ?? '').slice(0, h.from) +
          replacementText +
          (child.text ?? '').slice(h.to);

        const updatedUtterance: DocumentTranscriptUtteranceV1 = {
          ...utt,
          children: utt.children.map((p, pi) =>
            pi !== h.pIdx
              ? p
              : {
                  ...p,
                  children: p.children.map((c, ci) =>
                    ci !== h.cIdx ? c : { ...c, text: newText }
                  )
                }
          )
        };

        const nextDoc = {
          ...document,
          document: document.document.map((u, ui) =>
            ui === uIdx ? updatedUtterance : u
          )
        };

        updateDocument(nextDoc);

        // 3) IMPORTANT: recompute hits using nextDoc (not the old state)
        const updatedHits = findHitsInDoc(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          nextDoc as any,
          search ?? '',
          !!caseSensitive,
          !!wholeWord
        );

        if (!updatedHits.length) {
          setHits([]);
          setActiveHitIndex(-1);
          setCurrentMatch(null);
          return;
        }

        const afterIdx = updatedHits.findIndex(
          (_, idx) => idx > activeHitIndex
        );
        const nextIdx = afterIdx !== -1 ? afterIdx : 0;

        setHits(updatedHits);
        setActiveHitIndex(nextIdx);

        const nh = updatedHits[nextIdx];
        const nextUtterance = nextDoc.document.find(
          uut =>
            uut.start === nh.utteranceStart && uut.stop === nh.utteranceStop
        );
        if (nextUtterance?.children[nh.pIdx]?.children[nh.cIdx]) {
          const nextChild = nextUtterance.children[nh.pIdx].children[nh.cIdx];
          setCurrentMatch(nextChild);
          focusMatch?.(nextChild);
        }
      }
    }
  };

  /** Replace ALL occurrences (bulk) — unchanged */
  const handleReplaceAll = () => {
    if (!document || !search || replacement === undefined) return;

    const rx = new RegExp(
      wholeWord ? `\\b${search}\\b` : search,
      caseSensitive ? 'g' : 'gi'
    );

    const utterances: DocumentTranscriptUtteranceV1[] = document.document.map(
      u => ({
        ...u,
        children: u.children.map(paragraph => ({
          ...paragraph,
          children: paragraph.children.map(child => ({
            ...child,
            text: (child.text ?? '').replace(rx, replacement)
          }))
        }))
      })
    );

    replaceAllWordsInEditor(search, !!caseSensitive, !!wholeWord, replacement);
    updateDocument({ ...document, document: utterances });
  };

  const hasQuery = (search ?? '').trim().length > 0;
  const hasResults = hits.length > 0;

  return (
    <section
      aria-label={t('replace')}
      className="h-full flex flex-col"
      style={vars}
    >
      <div className="flex-1 overflow-auto p-2 space-y-2">
        {/* Row 1: search input */}
        <div className="relative">
          <div className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none">
            <Search size={14} style={{ color: getColour('icon') }} />
          </div>
          <Input
            ref={searchInputRef}
            placeholder={t('search')}
            value={query}
            onChange={e => handleSearchInput(e.target.value)}
            type="text"
            autoComplete="off"
            className="block w-full h-8 text-xs pl-8 focus:ring-0! focus-visible:outline-none!"
          />
        </div>

        {/* Row 2: match-options left, navigate right */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <Tooltip label={t('case-sensitive')}>
              <button
                type="button"
                onClick={() => setCaseSensitive(!caseSensitive)}
                className="h-6 px-2 flex items-center justify-center rounded-2xl border text-xs font-medium transition-colors hover:opacity-80"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: caseSensitive
                    ? getColour('thirdLayer')
                    : 'transparent',
                  color: caseSensitive
                    ? getColour('activeText')
                    : getColour('normalText')
                }}
              >
                Aa
              </button>
            </Tooltip>
            <Tooltip label={t('whole-word')}>
              <button
                type="button"
                onClick={() => setWholeWord(!wholeWord)}
                className="h-6 px-2 flex items-center justify-center rounded-2xl border text-xs font-medium transition-colors hover:opacity-80"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: wholeWord
                    ? getColour('thirdLayer')
                    : 'transparent',
                  color: wholeWord
                    ? getColour('activeText')
                    : getColour('normalText')
                }}
              >
                Ww
              </button>
            </Tooltip>
          </div>
          <div
            className="inline-flex items-center rounded-2xl border overflow-hidden"
            style={{ borderColor: getColour('border') }}
          >
            <Tooltip label={t('previous-occurrence')}>
              <button
                type="button"
                onClick={onPrev}
                disabled={!hasResults}
                aria-label={t('previous-occurrence')}
                className="h-6 w-6 flex items-center justify-center transition-colors hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ color: getColour('normalText') }}
              >
                <ChevronLeft size={14} />
              </button>
            </Tooltip>
            <span
              className="px-2 h-6 flex items-center justify-center text-xs tabular-nums border-x"
              style={{
                borderColor: getColour('border'),
                color: hasResults
                  ? getColour('normalText')
                  : getColour('inactiveText'),
                minWidth: 40
              }}
              aria-live="polite"
            >
              {hasResults ? `${activeHitIndex + 1}/${hits.length}` : '0/0'}
            </span>
            <Tooltip label={t('next-occurrence')}>
              <button
                type="button"
                onClick={onNext}
                disabled={!hasResults}
                aria-label={t('next-occurrence')}
                className="h-6 w-6 flex items-center justify-center transition-colors hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ color: getColour('normalText') }}
              >
                <ChevronRight size={14} />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Row 3: replace input */}
        <Input
          id="replace"
          placeholder={t('enter-replacement-text')}
          value={replacement}
          onChange={e => setReplacement(e.target.value)}
          type="text"
          autoComplete="off"
          className="block w-full h-8 text-xs px-2"
        />

        {/* Row 4: replace-this + replace-all */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleReplaceThis}
            disabled={!hasQuery || !hasResults}
            className="text-xs font-medium transition-colors hover:underline disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
            style={{ color: getColour('activeText') }}
          >
            {t('replace-this')}
          </button>
          <button
            type="button"
            onClick={handleReplaceAll}
            disabled={!hasQuery || !hasResults}
            className="text-xs font-medium transition-colors hover:underline disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
            style={{ color: getColour('activeText') }}
          >
            {t('replace-all')}
          </button>
        </div>
      </div>
    </section>
  );
};
